import React from 'react';
import { Loader2 } from 'lucide-react';
import { fa, scoreLabel, surfaceClass } from './shared';

const tones = {
  blue: 'bg-blue-50 text-[#1C3B9B]',
  magenta: 'bg-[#FCF1F7] text-[#C11277]',
  slate: 'bg-slate-100 text-slate-500',
  green: 'bg-emerald-50 text-emerald-700',
};

export function MetricCard({ icon, label, value, note, tone = 'blue' }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; note: string; tone?: keyof typeof tones;
}) {
  return <div className={`${surfaceClass} min-w-0 p-4 sm:p-5`}>
    <div className="flex items-center justify-between gap-3"><p className="min-w-0 text-xs font-medium leading-6 text-slate-500 sm:text-sm">{label}</p><span className={`hidden h-9 w-9 shrink-0 items-center justify-center rounded-xl sm:flex ${tones[tone]}`}>{icon}</span></div>
    <div className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-[28px]">{value}</div>
    <p className="mt-1 text-[11px] leading-6 text-slate-500 sm:text-xs">{note}</p>
  </div>;
}

export function EmptyState({ icon, title, description, action }: {
  icon: React.ReactNode; title: string; description: string; action?: React.ReactNode;
}) {
  return <div className="flex flex-col items-center px-5 py-12 text-center sm:py-16">
    <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-blue-100 bg-blue-50 text-[#1C3B9B]">{icon}</span>
    <h3 className="text-base font-bold text-slate-900">{title}</h3>
    <p className="mt-2 max-w-md text-sm leading-7 text-slate-500">{description}</p>
    {action && <div className="mt-5">{action}</div>}
  </div>;
}

export function LoadingState({ label }: { label: string }) {
  return <div role="status" className={`${surfaceClass} overflow-hidden p-6 sm:p-8`}>
    <div className="mb-6 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={17} className="animate-spin text-[#1C3B9B]" />{label}</div>
    <div aria-hidden className="grid animate-pulse gap-4 sm:grid-cols-3">{[0, 1, 2].map(key => <div key={key} className="space-y-4 rounded-xl border border-slate-100 p-5"><div className="h-3 w-24 rounded bg-slate-100" /><div className="h-8 w-16 rounded bg-slate-100" /><div className="h-2.5 w-32 max-w-full rounded bg-slate-50" /></div>)}</div>
    <div aria-hidden className="mt-6 h-20 animate-pulse rounded-xl bg-slate-50" />
  </div>;
}

export function ScoreBadge({ score }: { score: number | null }) {
  return score === null ? <span className="inline-flex rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs text-slate-500">بدون نمره</span> : <span title={scoreLabel(score)} className="inline-flex min-w-20 items-baseline justify-center gap-1 rounded-lg border border-blue-100/80 bg-blue-50 px-2.5 py-1.5 text-[#1C3B9B]"><strong className="text-base font-bold">{fa(score)}</strong><span className="text-[10px] text-blue-700/70">/ ۱۰۰</span></span>;
}

export function DeltaBadge({ value }: { value: number | null }) {
  return <span dir="ltr" className={`inline-flex min-w-12 items-center justify-center rounded-lg px-2 py-1 text-xs font-semibold ${value === null ? 'text-slate-500' : value === 0 ? 'bg-slate-100 text-slate-500' : 'bg-blue-50 text-[#1C3B9B]'}`}>{value === null ? '—' : `${value > 0 ? '+' : ''}${fa(value)}`}</span>;
}
